/**
 * Process items with a sliding window concurrency limit.
 * Starts a new request immediately when one completes, maintaining maximum
 * throughput regardless of individual request timing.
 */
export async function processWithConcurrency<T>(
    items: T[],
    concurrency: number,
    handler: (item: T) => Promise<void>
): Promise<void> {
    if (concurrency <= 0) {
        throw new Error('Concurrency must be a positive number');
    }

    if (items.length === 0) {
        return;
    }

    let currentIndex = 0;

    const executeNext = async (): Promise<void> => {
        while (currentIndex < items.length) {
            const index = currentIndex++;
            const item = items[index] as T;

            try {
                await handler(item);
            } catch {
                // Continue processing other items even if one fails
            }
        }
    };

    // Start `concurrency` number of workers
    const workers = Array.from({ length: Math.min(concurrency, items.length) }, () => executeNext());

    await Promise.all(workers);
}
